import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  Input,
  Select,
  VStack,
  HStack,
  SimpleGrid,
  Flex,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Stack,
  Badge,
  Icon,
  useColorModeValue,
  useBreakpointValue,
  useToast,
  Spinner,
  ScaleFade,
  Alert,
  AlertIcon,
  AlertDescription,
  Image,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  Slide,
  IconButton,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FiBook, FiSearch, FiPlusCircle, FiGrid, FiFileText, FiArrowRight, FiPlus } from 'react-icons/fi';
import {
  getArticles,
  generateArticle,
  searchArticles,
  GenerateArticleParams,
} from '../services/smartReadApi';
import { ArticleCard } from '../components/smart-read/ArticleCard';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../lib/apiClient';
import { useTranslation } from 'react-i18next';

const MotionBox = motion(Box);

export function SmartRead() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { userLanguage } = useLanguage();
  const { user } = useAuth();
  const toast = useToast();
  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure();
  const { t } = useTranslation();

  // State for article generation form
  const [topic, setTopic] = useState('');
  const [length, setLength] = useState<'short' | 'long'>('short');
  const [isGeneratorOpen, setIsGeneratorOpen] = useState(false);
  const [targetLanguage, setTargetLanguage] = useState('');

  // State for search and filtering
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('');

  // Fetch user settings to get target language
  const { data: userSettings } = useQuery({
    queryKey: ['user-settings', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      try {
        const data = await apiClient.getUserSettings();
        return data?.settings || null;
      } catch (error) {
        console.error('Error fetching user settings:', error);
        return null;
      }
    },
    enabled: !!user?.id
  });

  // Set target language from user settings
  useEffect(() => {
    if (userSettings && userSettings.targetLanguage) {
      setTargetLanguage(userSettings.targetLanguage);
    } else {
      // Default to user's native language if target language is not set
      setTargetLanguage(userLanguage || 'english');
    }
  }, [userSettings, userLanguage]);

  // Color mode values
  const bgGradient = useColorModeValue(
    'linear(to-b, purple.50, white)',
    'linear(to-b, purple.900, gray.900)'
  );
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('purple.100', 'purple.700');
  const highlightColor = useColorModeValue('purple.500', 'purple.300');
  const textColor = useColorModeValue('gray.800', 'white');
  const secondaryText = useColorModeValue('gray.600', 'gray.400');
  const badgeBg = useColorModeValue('purple.100', 'purple.900');
  const badgeColor = useColorModeValue('purple.700', 'purple.200');
  const formBg = useColorModeValue('gray.50', 'gray.800');
  const buttonBg = useColorModeValue('purple.500', 'purple.400');
  const buttonHoverBg = useColorModeValue('purple.600', 'purple.500');
  const accentButtonBg = useColorModeValue('green.500', 'green.400');
  const accentButtonHoverBg = useColorModeValue('green.600', 'green.500');
  const inputBg = useColorModeValue('white', 'gray.700');
  const mobileActionBg = useColorModeValue('purple.500', 'purple.400');

  // Responsive column count
  const columns = useBreakpointValue({ base: 1, md: 2, lg: 3 }) || 1;
  const isMobile = useBreakpointValue({ base: true, md: false });

  // Fetch articles (with search/filter if applicable)
  const { data: articles = [], isLoading, error } = useQuery({
    queryKey: ['articles', searchQuery, selectedLanguage],
    queryFn: () => searchArticles({ query: searchQuery, language: selectedLanguage }),
  });

  // Mutation for generating a new article
  const {
    mutate: generateNewArticle,
    isPending: isGenerating,
    error: generationError,
  } = useMutation({
    mutationFn: (params: GenerateArticleParams) => generateArticle(params),
    onSuccess: (articleId) => {
      // Show success toast
      toast({
        title: t('smartRead.articleGeneratedSuccess'),
        description: t('smartRead.articleReadyToRead'),
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });

      // Invalidate articles query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['articles'] });

      // Navigate to the newly created article
      navigate(`/smart-read/${articleId}`);

      // Reset form
      setTopic('');
      setLength('short');
      setIsGeneratorOpen(false);
      onDrawerClose(); // Close drawer if it's open
    },
    onError: (error: Error) => {
      console.error('Article generation failed:', error);

      // Show detailed error toast
      toast({
        title: t('smartRead.generationError'),
        description: `${t('errors.somethingWentWrong')}: ${error.message}. ${t('smartRead.tryAgain')}`,
        status: 'error',
        duration: 7000,
        isClosable: true,
        position: 'top',
      });
    }
  });

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!topic.trim()) return;

    try {
      // Capitalize each word in the topic
      const capitalizedTopic = topic
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      // Use targetLanguage instead of userLanguage for article generation
      generateNewArticle({
        topic: capitalizedTopic,
        length,
        language: targetLanguage || userLanguage,
        nativeLanguage: userLanguage
      });
    } catch (error) {
      console.error('Error submitting article generation:', error);
    }
  };

  // Handle opening the creation form - either in drawer (mobile) or in-place (desktop)
  const handleOpenCreateForm = () => {
    if (isMobile) {
      onDrawerOpen();
    } else {
      setIsGeneratorOpen(true);
    }
  };

  return (
    <Container maxW={{ base: "container.sm", md: "container.xl" }} py={8} px={{ base: 4, md: 8 }}>
      {/* Page Header */}
      <Heading
        as="h1"
        display="flex"
        alignItems="center"
        bgGradient="linear(to-l, #7928CA, #FF0080)"
        bgClip="text"
        fontWeight="extrabold"
        fontSize={{ base: "2xl", md: "3xl" }}
        mb={{ base: 6, md: 8 }}
        textAlign="left"
      >
        <Icon as={FiBook} mr={2} />
        {t('smartRead.title')}
      </Heading>

      <VStack spacing={8} align="stretch">
        {/* Search and Filter Section */}
        <Card
          bg={cardBg}
          borderColor={cardBorder}
          borderWidth="1px"
          borderRadius="xl"
          shadow="md"
          mb={8}
          overflow="hidden"
        >
          <CardBody>
            <VStack spacing={5}>
              <Flex
                w="full"
                direction={{ base: "column", md: "row" }}
                gap={4}
              >
                <InputGroup flexGrow={1}>
                  <CustomInput
                    placeholder={t('smartRead.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                    bg={inputBg}
                    size="lg"
                    borderRadius="md"
                    leftElement={<Icon as={FiSearch} color="gray.400" ml={3} />}
                  />
                </InputGroup>

                <Select
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  bg={inputBg}
                  size="lg"
                  borderRadius="md"
                  w={{ base: "full", md: "200px" }}
                >
                  <option value="">{t('smartRead.allLanguages')}</option>
                  <option value="en">{t('common.languages.english')}</option>
                  <option value="he">{t('common.languages.hebrew')}</option>
                  <option value="nl">{t('common.languages.dutch')}</option>
                </Select>

                {!isMobile && (
                  <Button
                    onClick={() => setIsGeneratorOpen(!isGeneratorOpen)}
                    leftIcon={<Icon as={isGeneratorOpen ? FiGrid : FiPlusCircle} />}
                    bg={buttonBg}
                    _hover={{ bg: buttonHoverBg }}
                    color="white"
                    size="lg"
                    borderRadius="md"
                    w={{ base: "full", md: "auto" }}
                  >
                    {isGeneratorOpen ? t('smartRead.viewArticles') : t('smartRead.createArticle')}
                  </Button>
                )}
              </Flex>

              {/* Desktop Article generation form */}
              {!isMobile && isGeneratorOpen && (
                <ScaleFade initialScale={0.95} in={isGeneratorOpen}>
                  <Box
                    w="full"
                    bg={formBg}
                    p={6}
                    borderRadius="lg"
                    borderWidth="1px"
                    borderColor={cardBorder}
                  >
                    <form onSubmit={handleSubmit}>
                      <VStack spacing={5} align="stretch">
                        <Heading size="md" color={textColor}>
                          {t('smartRead.generateArticle')}
                        </Heading>

                        <FormControl isRequired>
                          <FormLabel fontWeight="medium">
                            {t('smartRead.whatToReadAbout')}
                          </FormLabel>
                          <Input
                            placeholder={t('smartRead.topicPlaceholder')}
                            value={topic}
                            onChange={(e) => setTopic(e.target.value)}
                            bg={inputBg}
                            size="lg"
                            borderRadius="md"
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel fontWeight="medium">{t('smartRead.length')}</FormLabel>
                          <RadioGroup value={length} onChange={(val: 'short' | 'long') => setLength(val)}>
                            <Stack direction="row" spacing={5}>
                              <Radio colorScheme="purple" value="short">
                                <HStack>
                                  <Text>{t('smartRead.short')}</Text>
                                  <Badge colorScheme="purple">150-200 {t('common.words')}</Badge>
                                </HStack>
                              </Radio>
                              <Radio colorScheme="purple" value="long">
                                <HStack>
                                  <Text>{t('smartRead.long')}</Text>
                                  <Badge colorScheme="purple">400-500 {t('common.words')}</Badge>
                                </HStack>
                              </Radio>
                            </Stack>
                          </RadioGroup>
                        </FormControl>

                        <Button
                          type="submit"
                          isLoading={isGenerating}
                          loadingText={t('smartRead.generating')}
                          bg={accentButtonBg}
                          _hover={{ bg: accentButtonHoverBg }}
                          color="white"
                          size="lg"
                          borderRadius="md"
                          leftIcon={<Icon as={FiFileText} />}
                          rightIcon={<Icon as={FiArrowRight} />}
                          isDisabled={!topic.trim()}
                        >
                          {t('smartRead.generateArticle')}
                        </Button>

                        {generationError && (
                          <Alert status="error" borderRadius="md">
                            <AlertIcon />
                            <AlertDescription>
                              {t('smartRead.generationError')}: {generationError.message || t('smartRead.tryAgain')}
                            </AlertDescription>
                          </Alert>
                        )}
                      </VStack>
                    </form>
                  </Box>
                </ScaleFade>
              )}
            </VStack>
          </CardBody>
        </Card>

        {/* Articles List Section */}
        {isLoading ? (
          <Flex justify="center" py={10}>
            <VStack spacing={4}>
              <Spinner size="xl" thickness="4px" color={highlightColor} />
              <Text color={secondaryText}>{t('smartRead.loading')}</Text>
            </VStack>
          </Flex>
        ) : error ? (
          <Alert status="error" borderRadius="lg" my={10}>
            <AlertIcon />
            <AlertDescription>
                {t('errors.failedToLoad')}. {t('smartRead.tryAgain')}.
            </AlertDescription>
          </Alert>
        ) : articles.length === 0 ? (
          <Card
            bg={cardBg}
            borderColor={cardBorder}
            borderWidth="1px"
            borderRadius="xl"
            shadow="md"
            p={10}
            textAlign="center"
          >
            <VStack spacing={4}>
              <Icon as={FiFileText} boxSize={12} color={secondaryText} />
              <Text fontSize="xl" color={secondaryText}>
                    {t('smartRead.noArticles')}
              </Text>
              <Button
                    onClick={handleOpenCreateForm}
                leftIcon={<Icon as={FiPlusCircle} />}
                colorScheme="purple"
                size="lg"
                mt={4}
              >
                    {t('smartRead.createFirst')}
              </Button>
            </VStack>
          </Card>
        ) : (
          <SimpleGrid columns={columns} spacing={6}>
            {articles.map((article, index) => (
              <MotionBox
                key={article.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <ArticleCard article={article} />
              </MotionBox>
            ))}
          </SimpleGrid>
        )}

        {/* Mobile Floating Action Button */}
        {isMobile && (
          <Box
            position="fixed"
            bottom="20px"
            right="20px"
            zIndex={3}
          >
            <IconButton
              aria-label={t('smartRead.createArticle')}
              icon={<FiPlus />}
              size="lg"
              fontSize="24px"
              bg={mobileActionBg}
              color="white"
              borderRadius="full"
              height="60px"
              width="60px"
              boxShadow="0 4px 12px rgba(0,0,0,0.15)"
              _hover={{
                bg: buttonHoverBg,
                transform: "scale(1.05)",
              }}
              _active={{
                bg: buttonHoverBg,
                transform: "scale(0.95)",
              }}
              onClick={onDrawerOpen}
            />
          </Box>
        )}

        {/* Mobile Article Creation Drawer */}
        <Drawer isOpen={isDrawerOpen} onClose={onDrawerClose} placement="bottom" size="full">
          <DrawerOverlay />
          <DrawerContent borderTopRadius="2xl">
            <DrawerCloseButton size="lg" top="20px" />
            <DrawerHeader
              borderBottomWidth="1px"
              fontSize="xl"
              fontWeight="bold"
              py={6}
            >
              {t('smartRead.createArticle')}
            </DrawerHeader>
            <DrawerBody p={6}>
              <form onSubmit={handleSubmit}>
                <VStack spacing={6} align="stretch">
                  <FormControl isRequired>
                    <FormLabel fontWeight="medium" fontSize="lg">
                      {t('smartRead.whatToReadAbout')}
                    </FormLabel>
                    <Input
                      placeholder={t('smartRead.topicPlaceholder')}
                      value={topic}
                      onChange={(e) => setTopic(e.target.value)}
                      bg={inputBg}
                      size="lg"
                      borderRadius="md"
                      fontSize="lg"
                      height="60px"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel fontWeight="medium" fontSize="lg">
                      {t('smartRead.length')}
                    </FormLabel>
                    <RadioGroup value={length} onChange={(val: 'short' | 'long') => setLength(val)}>
                      <Stack direction="column" spacing={4}>
                        <Radio colorScheme="purple" value="short" size="lg">
                          <VStack align="flex-start" spacing={0}>
                            <Text fontSize="lg">{t('smartRead.short')}</Text>
                            <Text fontSize="sm" color={secondaryText}>150-200 {t('common.words')}</Text>
                          </VStack>
                        </Radio>
                        <Radio colorScheme="purple" value="long" size="lg">
                          <VStack align="flex-start" spacing={0}>
                            <Text fontSize="lg">{t('smartRead.long')}</Text>
                            <Text fontSize="sm" color={secondaryText}>400-500 {t('common.words')}</Text>
                          </VStack>
                        </Radio>
                      </Stack>
                    </RadioGroup>
                  </FormControl>

                  <Box pt={4}>
                    <Button
                      type="submit"
                      isLoading={isGenerating}
                      loadingText={t('smartRead.generating')}
                      bg={accentButtonBg}
                      _hover={{ bg: accentButtonHoverBg }}
                      color="white"
                      size="lg"
                      borderRadius="md"
                      height="60px"
                      fontSize="lg"
                      width="full"
                      leftIcon={<Icon as={FiFileText} boxSize={5} />}
                      isDisabled={!topic.trim()}
                    >
                      {t('smartRead.generateArticle')}
                    </Button>
                  </Box>

                  {generationError && (
                    <Alert status="error" borderRadius="md" mt={4}>
                      <AlertIcon />
                      <AlertDescription>
                        {t('smartRead.generationError')}: {generationError.message || t('smartRead.tryAgain')}
                      </AlertDescription>
                    </Alert>
                  )}
                </VStack>
              </form>
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      </VStack>
    </Container>
  );
}

// Helper component for input with icon
const InputGroup = ({ children, ...props }: any) => {
  return (
    <Box position="relative" {...props}>
      {children}
    </Box>
  );
};

// Helper component for input icon
const CustomInput = ({ leftElement, ...props }: any) => {
  return (
    <Box position="relative">
      {leftElement && (
        <Box position="absolute" left={0} top={0} height="100%" display="flex" alignItems="center" zIndex={2}>
          {leftElement}
        </Box>
      )}
      <Input pl={leftElement ? 10 : 4} {...props} />
    </Box>
  );
};